import { format as frmt } from '@/utils/date-fns'
import { useState } from 'react'
import type { FAIconProps } from './fa-icon'
import { FAIcon } from './fa-icon'
import { Button, Calendar, Popover, PopoverContent, PopoverTrigger } from './ui'
import { cn } from '@/lib/utils'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { DateRange } from 'react-day-picker'
import { addYears, endOfYear } from 'date-fns'

interface CommonProps {
  id?: string
  className?: string
  triggerClassName?: string

  placeholder?: string
  triggerIcon?: FAIconProps
  disabled?: boolean
  format?: string
  hideWeekNumbers?: boolean
}

interface SingleProps extends CommonProps {
  mode: 'single'
  value: Date | null
  onChange(date: Date | null): void
}

interface RangeProps extends CommonProps {
  mode: 'range'
  value: DateRange | null
  onChange(dates: DateRange | null): void
}

type Props = SingleProps | RangeProps

export function DatePicker({
  id,
  className,
  triggerClassName,

  mode,
  value,

  placeholder,
  triggerIcon,
  disabled = false,
  format = 'PPP',
  hideWeekNumbers = false,

  onChange,
}: Props) {
  const translations = useTranslate({
    placeholder: 'common.select-date',
  })

  const [open, setOpen] = useState(false)

  const label = (() => {
    if (!value) return placeholder ?? translations.placeholder

    if (mode === 'single') return frmt(value, format)

    const { from, to } = value
    if (!from) return placeholder ?? translations.placeholder

    return `${frmt(from, format)} - ${to ? frmt(to, format) : '?'}`
  })()

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            className={cn(
              'w-full justify-between px-4 bg-quaternary border-gray-200',
              'disabled:opacity-100',
              open && 'border-secondary hover:border-secondary',
              triggerClassName
            )}
            disabled={disabled}
          >
            {label}

            {(!!triggerIcon || !disabled) && (
              <FAIcon
                className="pl-4"
                icon={open ? 'chevron-up' : 'chevron-down'}
                {...triggerIcon}
              />
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-auto overflow-hidden p-0" align="start">
          {mode === 'single' ? (
            <Calendar
              mode="single"
              captionLayout="dropdown"
              selected={value ?? undefined}
              defaultMonth={value ?? undefined}
              startMonth={new Date(1900, 0, 1)}
              endMonth={endOfYear(addYears(new Date(), 5))}
              showWeekNumber={!hideWeekNumbers}
              onSelect={value => {
                onChange(
                  !value
                    ? null
                    : new Date(
                        value.getFullYear(),
                        value.getMonth(),
                        value.getDate(),
                        12, // Try to avoid timezone issues with pure dates
                        0
                      )
                )
                setOpen(false)
              }}
            />
          ) : (
            <Calendar
              mode="range"
              captionLayout="dropdown"
              selected={value ?? undefined}
              defaultMonth={value?.from}
              numberOfMonths={2}
              startMonth={new Date(2000, 0, 1)}
              endMonth={endOfYear(addYears(new Date(), 5))}
              showWeekNumber={!hideWeekNumbers}
              onSelect={value => {
                onChange(value ?? null)
              }}
            />
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
}
